import type { CustomerQuoteDetail } from '@repo/api-contracts';
import { ArrowLeft } from 'lucide-react';
import type { ReactElement, Ref } from 'react';
import { Link } from 'react-router';
import { QuoteStatusBadge } from '@/entities/quote';
import { MY_QUOTES_PATH } from '@/shared/config';
import { formatDate, formatDateTime } from '@/shared/lib';

export interface QuoteDetailHeaderProps {
  readonly detail: CustomerQuoteDetail;
  /** 상태가 바뀌면(수정 요청 보냄) 상세가 이 배지로 포커스를 옮긴다. */
  readonly statusRef: Ref<HTMLSpanElement>;
}

/** 상세 머리 — 목록으로 돌아가는 링크, 큰 견적 번호와 상태 배지, 요청·발행 날짜. */
export const QuoteDetailHeader = ({ detail, statusRef }: QuoteDetailHeaderProps): ReactElement => {
  const issued = detail.latestIssuedVersion;

  return (
    <header className="flex flex-col gap-8">
      <Link
        to={MY_QUOTES_PATH}
        className="inline-flex w-fit items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <ArrowLeft aria-hidden className="size-4" strokeWidth={1.5} />내 견적
      </Link>

      <div className="flex flex-col gap-5 border-b border-foreground/10 pb-8 sm:flex-row sm:items-end sm:justify-between">
        <h1 className="flex flex-col gap-3">
          <span className="text-[11px] leading-none font-semibold tracking-[0.18em] text-muted-foreground uppercase">
            견적
          </span>{' '}
          <span className="text-[clamp(2.25rem,4.5vw,4rem)] leading-none font-bold tracking-[-0.03em] tabular-nums">
            {detail.quoteNumber}
          </span>
        </h1>

        <div className="flex flex-col gap-3 sm:items-end">
          <QuoteStatusBadge
            ref={statusRef}
            tabIndex={-1}
            status={detail.status}
            orderDisplayStatus={detail.orderDisplayStatus}
            className="w-fit"
          />
          <dl className="flex flex-wrap gap-x-5 gap-y-1 text-sm text-muted-foreground tabular-nums">
            <div className="flex gap-2">
              <dt>요청일</dt>
              <dd className="text-foreground">{formatDateTime(detail.createdAt)}</dd>
            </div>
            {issued !== null ? (
              <div className="flex gap-2">
                <dt>최근 발행</dt>
                <dd className="text-foreground">{formatDate(issued.issuedAt)}</dd>
              </div>
            ) : null}
          </dl>
        </div>
      </div>
    </header>
  );
};
